import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import {
  AppContext,
  toast,
  getBrowserType,
  useStateCallback,
  COMMON_CONSTANTS,
  useIsAuthorisedHook
} from '@nile/components';
import styled from 'styled-components';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import { HomeProvider } from '@nile/mangifera/src/Contexts/HomeContext';
import TabManager from '@nile/mangifera/src/Containers/TabManager';
import { withRouter } from 'react-router-dom';

import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { GET_CLIENT_USER_ASSOCIATIONS_LIST } from '@nile/graphql/src/Query/client_management/client';
import {
  CREATE_TASK,
  MERGE_TICKET,
  SAVE_TICKET_CONVERSATION,
  SOFT_TICKET_DELETE,
  UPDATE_TICKETS,
  MARKSPAM_TICKETS,
  SAVE_PERSONALIZATION,
  AccountClient,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import { ERROR_MAP } from '@nile/mangifera/src/Constants/Tickets';
import { cloneDeep } from 'lodash';
import {
  getTabId,
  getTabUrl,
  getTabDisplayData,
  getTabContent,
  getTabTemplate,
  getTabUrlNew
} from '@nile/mangifera/src/Pebbles/TabManager';
import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { Frame } from '@shopify/polaris';
import AppNavigation from '@nile/components/src/Navigations/AppNavigation';
import envConfig from '@nile/home/src/Config/env';
import {
  isFeatureAvailable,
  useCheckFeatureGating
} from '@nile/components/src/HOC/withPlanFeatureGating';
import { getTabPersonalisationCategory } from '@nile/components/src/Utils/helpers';

function ClientHomePage(props) {
  const appContext = useContext(AppContext);
  const [homeOverlay, setHomeOverlay] = useState(false);
  const [modulesOverlay, setModulesOverlay] = useState(false);
  const [createNewOverlay, setCreateNewOverlay] = useState(false);
  const [multiSiteAssociations, setMultiSiteAssociations] = useState([]);
  const [userAppState, setUserAppState] = useStateCallback(null);
  const { currentServicePortal, currentProduct } = appContext;
  const currentUser = appContext.user;
  const clientData = currentUser?.associations?.[0]?.client;
  const isKnowledgeBaseEnabled = useCheckFeatureGating('KNOWLEDGE_BASE')
    ?.enabled;
  const [selectedModule, setSelectedModule] = useState(null);
  const isTicketing = isFeatureAvailable(
    appContext?.planFeatureList,
    'TICKETING'
  );
  const isClientViewAuthorised = useIsAuthorisedHook('client.view');
  const flags = useFlags();
  const closeModulesOverlay = () => {
    setModulesOverlay(false);
  };
  const closeHomeOverlay = () => {
    setHomeOverlay(false);
  };
  const closeCreateNewOverlay = () => {
    setCreateNewOverlay(false);
  };
  const toggleHomeOverlay = useCallback(() => {
    closeModulesOverlay();
    closeCreateNewOverlay();
    setHomeOverlay(!homeOverlay);
  });
  const toggleModulesOverlay = useCallback(() => {
    closeHomeOverlay();
    closeCreateNewOverlay();
    setModulesOverlay(!modulesOverlay);
  });
  const toggleCreateNewOverlay = useCallback(() => {
    closeModulesOverlay();
    closeHomeOverlay();
    setCreateNewOverlay(!createNewOverlay);
  });

  const tabRef = useRef();

  const userAppStateRef = useRef();
  userAppStateRef.current = userAppState;

  const updateUserAppState = (newAppState, cb) => {
    if (cb) {
      setUserAppState(newAppState, cb);
    } else {
      setUserAppState(newAppState);
    }
  };
  const updateSelectedModule = (newAppState) => {
    setSelectedModule(newAppState);
  };
  const switchTabFocus = (tab, newAppState = []) => {
    tabRef?.current?.switchActiveTab(tab, newAppState);
  };

  const getCurrentAppState = () => {
    return tabRef.current.getCurrentState();
  };

  const [getMultiSiteAssociations] = useLazyQuery(
    GET_CLIENT_USER_ASSOCIATIONS_LIST,
    {
      client: AccountClient,
      variables: {
        input: {
          listInfo: {
            sort: {
              attribute: 'user.name',
              order: 'ASC'
            },
            condition: {
              joinOperator: 'AND',
              operands: [
                {
                  joinOperator: 'AND',
                  operands: [
                    {
                      value: appContext?.user?.userId || '',
                      operator: 'is',
                      operatorValue: null,
                      attribute: 'user.userId'
                    }
                  ]
                },
                {
                  joinOperator: 'AND',
                  operands: [
                    {
                      value: '1',
                      operator: 'is',
                      operatorValue: null,
                      attribute: 'primarySite'
                    }
                  ]
                }
              ]
            }
          }
        }
      },
      onCompleted(data) {
        setMultiSiteAssociations(
          data?.getClientUserAssociationsList?.associations
        );
      }
    }
  );

  useEffect(() => {
    if (
      currentUser?.accountType === 'CLIENT' &&
      currentProduct === COMMON_CONSTANTS.MSP &&
      isClientViewAuthorised
    ) {
      getMultiSiteAssociations();
    }
  }, [currentUser?.accountType, currentProduct]);
  const {
    loading: userAppStateLoading,
    error: userAppStateError,
    data: peronalizedData
  } = useQuery(GET_PERSONALIZATION_BY_NAME, {
    variables: {
      userId: currentUser.userId,
      name: 'tabPersonalization',
      category: getTabPersonalisationCategory(
        currentServicePortal,
        currentProduct
      )
    },
    client: AccountClient,
    onCompleted(res) {
      if (res?.getPersonalizationByCategoryAndName?.value) {
        const appStateFromDb = JSON.parse(
          res?.getPersonalizationByCategoryAndName?.value
        );
        if (!isTicketing) {
          appStateFromDb.appState = appStateFromDb.appState.filter(
            (item) => item.id !== 'workspace'
          );
        }
        if (appStateFromDb.appState.length === 0) {
          appStateFromDb.appState.push({
            id: 'profile',
            content: getTabTemplate({ module: 'PROFILE' }),
            accessibilityLabel: 'My profile',
            module: 'PROFILE',
            tabId: 8,
            state: {},
            data: {},
            url: '/profile/detail/edit'
          });
        }

        const moduleTabIdx = appStateFromDb?.appState?.findIndex(
          ({ url }) => url === '/ticket/1/ticket-list'
        );

        if (moduleTabIdx > -1) {
          appStateFromDb.appState[moduleTabIdx].url = '/ticket/1/ticket-list';
        }

        setUserAppState(
          {
            ...appStateFromDb
          },
          () => {
            const newState = appStateFromDb;
            if (newState?.appState[newState?.activeTabIndex])
              switchTabFocus(newState.appState[newState.activeTabIndex]);
          }
        );
      } else {
        const currentAppState = userAppState || { appState: [] };
        const updatedAppState = cloneDeep(currentAppState.appState);
        let tab = isKnowledgeBaseEnabled
          ? {
              id: 'CLIENTHOMEKB',
              url: '/client-home',
              name: 'CLIENTHOMEKB',
              accessibilityLabel: 'Home',
              content: getTabTemplate({ module: 'CLIENTHOMEKB' }),
              module: 'CLIENTHOMEKB'
            }
          : {
              id: 'workspace',
              content: getTabTemplate({ module: 'WORKSPACE' }),
              accessibilityLabel: 'My workspace',
              module: 'WORKSPACE',
              tabId: 8,
              state: {},
              data: {},
              url: '/ticket/1/ticket-list'
            };
        if (!isTicketing) {
          tab = updatedAppState.find((el) => el.id === 'profile');
          if (!tab) {
            tab = {
              id: 'profile',
              content: getTabTemplate({ module: 'PROFILE' }),
              accessibilityLabel: 'My profile',
              module: 'PROFILE',
              tabId: 8,
              state: {},
              data: {},
              url: '/profile/detail/edit'
            };
            updatedAppState.push(tab);
            updateUserAppState(
              {
                ...currentAppState,
                appState: updatedAppState,
                activeTabIndex: updatedAppState.findIndex(
                  (el) => el.id === tab.id
                )
              },
              () => {
                switchTabFocus(tab);
              }
            );
          }
        } else {
          updatedAppState.push(tab);
          updateUserAppState(
            {
              ...currentAppState,
              appState: updatedAppState,
              activeTabIndex: updatedAppState.findIndex(
                (el) => el.id === tab.id
              )
            },
            () => {
              switchTabFocus(tab);
            }
          );
        }
      }
    },
    onError(err) {}
  });
  const toggleOverlays = () => {
    switch (props?.match?.url) {
      case '/home':
        toggleHomeOverlay();
        break;
      case '/allmodules':
        toggleModulesOverlay();
        break;
      case '/createNew':
        toggleCreateNewOverlay();
      default:
        break;
    }
  };

  useEffect(() => {
    toggleOverlays();
    // Load respective pages
    const currentAppState = userAppStateRef.current || { appState: [] };
    const updatedAppState = cloneDeep(currentAppState.appState);
    let tab = null;
    if (props.match?.url.startsWith(`/client/${clientData?.accountId}`)) {
      tab = updatedAppState.find(
        (el) => el.id === `client-${clientData?.accountId}`
      );
      if (!tab) {
        tab = {
          id: `client-${clientData.accountId}`,
          content: getTabTemplate({
            module: 'CLIENT',
            data: {
              value: clientData.name || 'Client',
              name: clientData.name || 'Client',
              id: clientData.accountId
            }
          }),
          accessibilityLabel: 'Client tab',
          module: 'CLIENT',
          tabId: 2,
          state: {},
          data: {
            value: clientData.name || 'Client',
            name: clientData.name || 'Client',
            id: clientData.accountId
          },
          url: `/client/${clientData.accountId}/detail`
        };
        updatedAppState.push(tab);
        updateUserAppState(
          {
            ...currentAppState,
            appState: updatedAppState,
            activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
          },
          () => {
            switchTabFocus(tab);
          }
        );
        closeModulesOverlay();
      }
    } else if (
      props.match?.url.startsWith(`/ticket`) &&
      props.match?.url.endsWith('new')
    ) {
      tab = updatedAppState.find((el) => el.id === 'new-tab');
      if (!tab) {
        tab = {
          id: 'new-tab',
          content: getTabTemplate({ module: 'NEW' }),
          module: 'NEW',
          tabId: 'new-tab',
          state: {},
          data: {},
          url: '/ticket/aWqgT35s/new'
        };
        updatedAppState.push(tab);
        updateUserAppState(
          {
            ...currentAppState,
            appState: updatedAppState,
            activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
          },
          () => {
            switchTabFocus(tab);
          }
        );
        closeModulesOverlay();
      }
    } else if (
      props.match?.url.startsWith(`/ticket/`) ||
      props.match?.url.startsWith(`/modules`)
    ) {
      tab = updatedAppState.find((el) => el.id === 'workspace');
      if (!tab) {
        tab = {
          id: 'workspace',
          content: getTabTemplate({ module: 'WORKSPACE' }),
          accessibilityLabel: 'My workspace',
          module: 'WORKSPACE',
          tabId: 8,
          state: {},
          data: {},
          url: '/ticket/1/ticket-list'
        };
        updatedAppState.push(tab);
        updateUserAppState(
          {
            ...currentAppState,
            appState: updatedAppState,
            activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
          },
          () => {
            switchTabFocus(tab);
          }
        );
        closeModulesOverlay();
      } else if (
        props.match?.url.endsWith(`All Tickets`) ||
        props.match?.url.startsWith(`/modules`)
      ) {
        const newUpdatedAppState = getCurrentAppState()?.tabs || [];
        const tabIdx = newUpdatedAppState.findIndex((el) => el.id === tab.id);
        newUpdatedAppState[tabIdx] = {
          ...tab,
          url: '/ticket/1/ticket-list'
        };
        updateUserAppState(
          {
            ...currentAppState,
            appState: newUpdatedAppState,
            activeTabIndex: newUpdatedAppState.findIndex(
              (el) => el.id === tab.id
            )
          },
          () => {
            switchTabFocus(tab);
          }
        );
      }
    } else if (
      props.match?.url.startsWith('/profile/detail/edit') ||
      props.match?.url.startsWith('/clientportal')
    ) {
      tab = updatedAppState.find((el) => el.id === 'profile');
      if (!tab) {
        tab = {
          id: 'profile',
          content: getTabTemplate({ module: 'PROFILE' }),
          accessibilityLabel: 'My profile',
          module: 'PROFILE',
          tabId: 8,
          state: {},
          data: {},
          url: '/profile/detail/edit'
        };
        updatedAppState.push(tab);
        updateUserAppState(
          {
            ...currentAppState,
            appState: updatedAppState,
            activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
          },
          () => {
            switchTabFocus(tab);
          }
        );
        closeModulesOverlay();
      }
    } else if (props.match?.url.startsWith('/client/rmm/itdoc')) {
      tab = updatedAppState.find((el) => el.id === 'client-it-doc');
      if (!tab) {
        tab = {
          id: 'client-it-doc',
          content: getTabTemplate({ module: 'CLIENT_IT_DOCS' }),
          accessibilityLabel: 'Documents',
          module: 'CLIENT_IT_DOCS',
          tabId: 901, // random number
          state: {},
          data: {},
          url: '/client/rmm/itdoc/alldocuments&&1001'
        };
        updatedAppState.push(tab);
        updateUserAppState(
          {
            ...currentAppState,
            appState: updatedAppState,
            activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
          },
          () => {
            switchTabFocus(tab);
          }
        );
        closeModulesOverlay();
      }
    } else if (props.match?.url.startsWith('/client/rmm/asset-view/')) {
      tab = updatedAppState.find((el) => el.id === 'rmm-home');
      if (!tab) {
        tab = {
          id: 'rmm-home',
          content: getTabTemplate({ module: 'RMM_HOME' }),
          accessibilityLabel: 'Assets',
          module: 'RMM_HOME',
          tabId: 9016, // random number
          state: {},
          data: {
            isClientPortal: true
          },
          url: '/client/rmm/asset-view/4001'
        };
        updatedAppState.push(tab);
        updateUserAppState(
          {
            ...currentAppState,
            appState: updatedAppState,
            activeTabIndex: updatedAppState.findIndex((el) => el.id === tab.id)
          },
          () => {
            switchTabFocus(tab);
          }
        );
        closeModulesOverlay();
      }
    }
  }, [props?.match?.url]);

  /** *********************************************************
   * TEMPORARY TO MAKE THINGS WORK AS EXISTING.
   * SHOULD SEPARATE AS TAB CONTEXT AND MODULE SPECIFIC CONTEXT
   ********************************************************** */

  const [activeView, setActiveView] = useState(null);
  const [previousView, setPreviousView] = useState(null);
  const [records, setRecords] = useState([]);
  const [personalization, setPersonalization] = useState('');
  const [viewConditions, setViewConditions] = useState(null);
  const [viewType, setViewType] = useState('list');
  const [projectViewType, setProjectViewType] = useState('list');

  const [mergeTickets, { loading, error }] = useMutation(MERGE_TICKET, {
    onCompleted({ mergeTickets }) {
      toast('success', 'Ticket merged successfully');
      setRecords([]);
    },
    onError(res) {
      let errorCode =
        res?.errors?.[0]?.extensions?.clientError?.[0]?.errorCode || '0';
      let errorMessage = ERROR_MAP[errorCode.toString()];
      toast('error', errorMessage);
    }
  });

  const [
    updateTickets,
    { loading: updating, error: updateError }
  ] = useMutation(UPDATE_TICKETS, {
    onCompleted({ markSpamTickets }) {
      toast('success', 'Tickets updated successfully');
      setRecords([]);
    }
  });

  const [
    softDeleteTickets,
    { loading: deletingTicket, error: errorDelete }
  ] = useMutation(SOFT_TICKET_DELETE, {
    onCompleted({ softDeleteTickets }) {
      toast('success', 'Ticket deleted successfully');
      setRecords([]);
    }
  });

  const [
    markSpamTickets,
    { loading: spamming, error: spamError }
  ] = useMutation(MARKSPAM_TICKETS, {
    onCompleted({ markSpamTickets }) {
      toast('success', 'Ticket spammed successfully');
      setRecords([]);
    }
  });

  const [createTask, { loading: taskLoading, error: taskError }] = useMutation(
    CREATE_TASK,
    {
      onCompleted({ createTask }) {
        toast('success', 'Task created successfully');
      }
    }
  );

  const [
    saveTicketConversation,
    { loading: conversationLoading, error: conversationError }
  ] = useMutation(SAVE_TICKET_CONVERSATION, {
    onCompleted({ saveTicketConversation }) {
      toast('success', 'New node created successfully');
      setRecords([]);
    }
  });

  /**
   * USER PERSONALIZTION
   */

  const [tabPersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient,
    onCompleted({ savePersonalization }) {
      if (savePersonalization) {
        // eslint-disable-line
      }
    },
    onError() {
      toast('error', 'Something went wrong');
    }
  });

  //TODO cleanup this logic
  if (
    userAppStateLoading ||
    (peronalizedData &&
      !userAppState &&
      peronalizedData.getPersonalizationByCategoryAndName)
  )
    return <div>loading...</div>;

  if (userAppStateError) return <div>error...</div>;

  const actions = {
    setActiveView: setActiveView,
    setPreviousView: setPreviousView,
    setPersonalization: setPersonalization,
    setViewConditions: setViewConditions,
    personalization: personalization,
    activeView: activeView,
    previousView: previousView,
    records: records,
    viewConditions: viewConditions,
    setRecords: setRecords,
    softDeleteTickets: softDeleteTickets,
    markSpamTickets: markSpamTickets,
    createTask: createTask,
    mergeTickets: mergeTickets,
    updateTickets: updateTickets,
    saveTicketConversation: saveTicketConversation,
    tabPersonalization: tabPersonalization,
    viewType,
    setViewType: setViewType,
    projectViewType,
    setProjectViewType: setProjectViewType
  };

  const openTab = (params) => {
    tabRef.current.openTabCb(params);
    closeHomeOverlay();
  };

  const createTab = (params) => {
    const currentState = userAppState?.appState
      ? cloneDeep(userAppState.appState)
      : [];
    const tab = {
      ...params,
      id: getTabId(params)
    };
    let dupTab = currentState.find((el) => el.id === tab.id);
    if (dupTab) {
      setUserAppState(
        {
          ...userAppState,
          appState: currentState,
          activeTabIndex: currentState.findIndex((el) => el.id === tab.id)
        },
        () => {
          switchTabFocus(tab);
        }
      );
      closeHomeOverlay();
      return;
    }
    tab.content = getTabContent({ ...params, currentState, tab });
    // tab.component = getTabComponent({ ...params, currentState, tab });
    tab.displayData = getTabDisplayData(params);
    tab.data = params.data;
    if (flags?.urlRedirectionForTabs) {
      tab.url = getTabUrlNew(params);
    } else {
      tab.url = getTabUrl(params);
    }
    tab.module = params.module;
    currentState.push(tab);
    if (!userAppState) {
      setUserAppState({ appState: currentState }, () => {
        switchTabFocus(tab);
      });
    } else {
      setUserAppState(
        {
          ...userAppState,
          appState: currentState,
          activeTabIndex: currentState.findIndex((el) => el.id === tab.id)
        },
        () => {
          switchTabFocus(tab);
        }
      );
      // setTimeout(() => {

      // }, 200);
    }
    closeHomeOverlay();
  };

  const providerValue = {
    userAppState,
    updateUserAppState: updateUserAppState,
    toggleHomeOverlay,
    closeHomeOverlay,
    closeModulesOverlay,
    closeCreateNewOverlay,
    switchTabFocus,
    openTab,
    createTab,
    user: appContext.user,
    selectedModule,
    updateSelectedModule,
    getBrowserType,
    multiSiteAssociations
  };

  if (userAppState) {
    return (
      <HomeProvider value={providerValue}>
        <Frame navigation={<AppNavigation conf={envConfig} />}>
          <TabManager
            overlay={homeOverlay || modulesOverlay || createNewOverlay}
            ref={tabRef}
            actions={actions}
          />
        </Frame>
      </HomeProvider>
    );
  }
  return null;
}

export default withRouter(ClientHomePage);
